/**
 * Super Admin — Audit Log tab.
 *
 * Reads /api/platform/audit-log?prefix=platform. — surfaces every
 * platform-level write (plan preset edits, Razorpay config changes,
 * public URL updates, org suspend/reactivate, super admin promote/demote,
 * enterprise enquiries, etc.).
 */
import { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import { ArrowClockwise } from "@phosphor-icons/react";

export default function AuditLogTab() {
  const [rows, setRows] = useState(null);
  const [prefix, setPrefix] = useState("platform.");
  const [limit, setLimit] = useState(100);
  const [loading, setLoading] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/platform/audit-log", { params: { prefix, limit } });
      setRows(data.rows || []);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, []);

  return (
    <div className="space-y-4" data-testid="audit-log-tab">
      <Card>
        <CardHeader>
          <CardTitle className="text-base font-display tracking-tight">Platform audit log</CardTitle>
          <p className="text-xs text-muted-foreground mt-1">
            Every super-admin write on plan presets, Razorpay config, billing, orgs and platform
            email sender is captured here. Filter by action prefix — leave blank to show every
            audit row (heavy).
          </p>
        </CardHeader>
        <CardContent className="space-y-3">
          <div className="flex items-center gap-2 flex-wrap">
            <Input
              value={prefix}
              onChange={e => setPrefix(e.target.value)}
              placeholder="platform."
              className="max-w-xs"
              data-testid="audit-log-prefix"
            />
            <Input
              type="number"
              value={limit}
              onChange={e => setLimit(Number(e.target.value) || 100)}
              className="w-24"
              data-testid="audit-log-limit"
            />
            <Button size="sm" onClick={load} disabled={loading} data-testid="audit-log-refresh">
              <ArrowClockwise size={13} className={"mr-1 " + (loading ? "animate-spin" : "")} /> Refresh
            </Button>
          </div>
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>When</TableHead>
                  <TableHead>Action</TableHead>
                  <TableHead>Actor</TableHead>
                  <TableHead>Entity</TableHead>
                  <TableHead>Meta</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {(rows || []).map(r => (
                  <TableRow key={r.id} data-testid={`audit-row-${r.id}`}>
                    <TableCell className="text-xs whitespace-nowrap">
                      {r.at ? new Date(r.at).toLocaleString() : "—"}
                    </TableCell>
                    <TableCell>
                      <Badge variant="outline" className="text-[10px] font-mono">{r.action}</Badge>
                    </TableCell>
                    <TableCell className="text-xs">{r.actor_email || r.actor_id || "—"}</TableCell>
                    <TableCell className="text-xs">
                      {r.entity_type ? <span className="font-mono">{r.entity_type}:{r.entity_id}</span> : "—"}
                    </TableCell>
                    <TableCell className="text-[11px] font-mono max-w-md truncate" title={JSON.stringify(r.meta || {})}>
                      {r.meta ? JSON.stringify(r.meta) : ""}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
            {rows && rows.length === 0 && (
              <div className="p-6 text-center text-sm text-muted-foreground">No entries.</div>
            )}
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
